import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, LogOut, Trash2 } from 'lucide-react';
import { AdminLogin } from './AdminLogin';
import { ShowSlotCard } from './ShowSlotCard';
import { ChangePassword } from './ChangePassword';
import { YouTubeControl } from './YouTubeControl';
import { GRBackdateControl } from './GRBackdateControl';
import { useIST } from '../hooks/useIST';
import { pad2 } from '../lib/shows';

const API = process.env.REACT_APP_BACKEND_URL;
const SLOTS = [
  { show: '11am', label: '11 AM', theme: '#3B82F6' },
  { show: '12pm', label: '12 PM', theme: '#EF4444' },
  { show: '5pm', label: '5 PM', theme: '#22C55E' },
  { show: '7pm', label: '7 PM', theme: '#A855F7' },
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('gr_admin_token')}` });

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const ist = useIST();
  const [results, setResults] = useState({});
  const [history, setHistory] = useState([]);
  const [leads, setLeads] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r, h, l] = await Promise.all([
        axios.get(`${API}/api/results/today`),
        axios.get(`${API}/api/history`, { headers: authHeaders() }),
        axios.get(`${API}/api/leads`, { headers: authHeaders() }),
      ]);
      const today = {};
      r.data.forEach((res) => {
        today[res.show] = res;
      });
      setResults(today);
      setHistory(h.data);
      setLeads(l.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('gr_admin_token');
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    const check = async () => {
      const token = localStorage.getItem('gr_admin_token');
      if (!token) return setChecking(false);
      try {
        const { data } = await axios.get(`${API}/api/auth/me`, { headers: authHeaders() });
        setUser(data);
      } catch {
        localStorage.removeItem('gr_admin_token');
      } finally {
        setChecking(false);
      }
    };
    check();
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  useEffect(() => {
    if (!user) return undefined;
    const refresh = () => {
      if (document.visibilityState === 'visible') load();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    const iv = setInterval(refresh, 20000);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      clearInterval(iv);
    };
  }, [user, load]);

  const handleAuthError = (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('gr_admin_token');
      setUser(null);
      return true;
    }
    return false;
  };

  const istToday = () => new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 10);

  const announceTG = async (body) => {
    try {
      const { telegramAnnounce } = await import('./resultImage');
      const h = authHeaders();
      delete h['Content-Type'];
      await telegramAnnounce(body.show, body.letter, body.digits, istToday(), h);
      toast.success('Telegram channel-la post ஆயிடுச்சு 📢');
    } catch {
      toast.error('Telegram post fail — bot channel-la admin-a இருக்கானு check பண்ணுங்க');
    }
  };

  const publish = async (body) => {
    try {
      await axios.post(`${API}/api/results`, body, { headers: authHeaders() });
      toast.success(`${body.show} result published — customer draw start ஆகும்!`);
      load();
      announceTG(body);
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err.response?.data?.detail || 'Publish failed');
      if (err.response?.status === 409) load();
    }
  };

  const edit = async (id, body) => {
    try {
      await axios.put(`${API}/api/results/${id}`, body, { headers: authHeaders() });
      toast.success('Result updated');
      load();
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const remove = async (id, show) => {
    try {
      await axios.delete(`${API}/api/results/${id}`, { headers: authHeaders() });
      toast.success(`${show} result deleted`);
      load();
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const touchedRef = useRef(null);
  const touch = async (show) => {
    if (touchedRef.current === show) return;
    touchedRef.current = show;
    try {
      await axios.post(`${API}/api/stage`, { show }, { headers: authHeaders() });
    } catch {
      touchedRef.current = null;
      toast.error('Stage switch fail — internet/login check பண்ணுங்க');
    }
  };

  const removeLead = async (id) => {
    try {
      await axios.delete(`${API}/api/leads/${id}`, { headers: authHeaders() });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success('Lead deleted');
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error('Delete failed');
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true, headers: authHeaders() });
    } catch {
      // ignore
    }
    localStorage.removeItem('gr_admin_token');
    setUser(null);
  };

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-[#07130A] text-[#F5C542]">Loading…</div>;
  if (!user) return <AdminLogin onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-[#07130A] px-6 py-10 md:px-12" data-testid="admin-panel">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Crown className="h-7 w-7 text-[#F5C542]" />
            <div>
              <h1 className="font-display text-2xl uppercase tracking-[0.2em] text-[#F5C542]">Golden Raja Admin</h1>
              <p className="text-xs text-[#A3A3A3]">{user.email} · Today&apos;s draw results</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="mr-2 border border-[#F5C542]/30 bg-black/40 px-4 py-2 text-right" data-testid="admin-live-clock">
              <p className="font-display text-lg leading-none tracking-[0.1em] text-[#F5C542]">
                {pad2(ist.hour % 12 || 12)}:{pad2(ist.minute)}:{pad2(ist.second)}{' '}
                <span className="text-sm">{ist.hour >= 12 ? 'PM' : 'AM'}</span>
              </p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">
                {ist.weekday.slice(0, 3)} {ist.day}.{ist.month}.{ist.year} · IST
              </p>
            </div>
            <ChangePassword authHeaders={authHeaders} />
            <button type="button" onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-[0.15em] text-white hover:border-red-400 hover:text-red-400">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>

        <YouTubeControl authHeaders={authHeaders} />

        <GRBackdateControl authHeaders={authHeaders} />

        <div className="grid gap-5 md:grid-cols-2">
          {SLOTS.map((s) => (
            <ShowSlotCard
              key={`${s.show}-${results[s.show]?.id || 'empty'}-${results[s.show]?.updated_at || ''}`}
              show={s.show}
              label={s.label}
              theme={s.theme}
              existing={results[s.show] || null}
              onPublish={publish}
              onEdit={edit}
              onDelete={remove}
              onTouch={touch}
            />
          ))}
        </div>

        <div className="mt-10">
          <h2 className="font-display mb-4 text-lg uppercase tracking-[0.2em] text-[#F5C542]">
            Customer Leads <span className="ml-2 text-sm text-[#A3A3A3]">({leads.length})</span>
          </h2>
          <div className="max-h-72 overflow-y-auto border border-white/10 bg-black/30" data-testid="admin-leads">
            {leads.length === 0 && <p className="p-4 text-sm text-[#666]">No leads yet — landing page form submissions varum inga</p>}
            {leads.map((l) => (
              <div key={l.id} className="flex items-center gap-4 border-b border-white/5 px-4 py-3 text-sm" data-testid={`lead-row-${l.id}`}>
                <span className="text-white">{l.email}</span>
                <span className="ml-auto text-xs text-[#666]">{new Date(l.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST</span>
                <button type="button" onClick={() => removeLead(l.id)} data-testid={`lead-delete-${l.id}`} className="text-red-400/70 hover:text-red-400">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10">
          <h2 className="font-display mb-4 text-lg uppercase tracking-[0.2em] text-[#F5C542]">History</h2>
          <div className="max-h-96 overflow-y-auto border border-white/10 bg-black/30" data-testid="admin-history">
            {history.length === 0 && <p className="p-4 text-sm text-[#666]">No actions yet</p>}
            {history.map((h) => (
              <div key={h.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-white/5 px-4 py-3 text-sm">
                <span className={`w-16 text-[11px] font-semibold uppercase tracking-[0.15em] ${h.action === 'delete' ? 'text-red-400' : h.action === 'edit' ? 'text-blue-400' : 'text-green-400'}`}>
                  {h.action}
                </span>
                <span className="text-white">
                  {h.detail.show} · {h.detail.date} · <span className="font-display text-[#F5C542]">{h.detail.letter} {h.detail.digits}</span>
                </span>
                <span className="ml-auto text-xs text-[#666]">{new Date(h.at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
